import { useState, useCallback, useEffect } from "react";
import { BigNumber } from "bignumber.js";
import config from "../config";
import useLiquidity from "./useLiquidityHelpers";
import useFarmCommon from "./useFarmCommon";
import { useTokenHelpers } from "./useTokenHelpers";
import { writeContract, waitForTransactionReceipt } from "@wagmi/core";
import { config as wagmiConfig } from "../../wagmi.config";
import useLazyReadContractAsync from "./useLazyReadContractAsync";
import { EarnOtherFixedAPR } from "../interfaces/config";
import { FixApr } from "../interfaces/config";
import { useUnifiedWallet } from "../providers/UnifiedWalletProvider";
import EarnOtherFixedAPRABI from "../assets/abi/farm/EarnOtherFixedAPR.json";

type Events = {
  onTransactionHash: (txHash: string) => void;
  onReceipt: (receipt: any) => void;
  onError: (error: any, receipt: any) => void;
};

const useAprHelper = () => {
  const { getBalanceOfLP } = useLiquidity();
  const { getPriceTokenExternalApi } = useFarmCommon();
  const { getAllowance, approveTokenToSpender } = useTokenHelpers();
  const [isReady, setIsReady] = useState(false);
  const { walletApi } = useUnifiedWallet();
  const { lazyReadContract } = useLazyReadContractAsync();

  useEffect(() => {
    setIsReady(true);
  }, []);

  const claimLpToken = useCallback(
    async (
      contractAddress: string,
      account: string,
      events: Events
    ): Promise<any> => {
      try {
        const hash = await writeContract(wagmiConfig, {
          abi: EarnOtherFixedAPRABI,
          address: contractAddress as `0x${string}`,
          functionName: "harvest",
          args: [],
        });
        
        // Wait for transaction to be mined
        const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });
        
        events.onReceipt({ transactionHash: receipt.transactionHash, status: receipt.status === "success" });
        return { transactionHash: receipt.transactionHash, status: receipt.status === "success" };
      } catch (e) {
        events.onError(e, null);
        return null;
      }
    },
    []
  );

  const depositLpToken = async (
    contractAddress: string,
    account: string,
    inputAmount: BigNumber,
    events: Events
  ): Promise<any> => {
    try {
      const hash = await writeContract(wagmiConfig, {
        abi: EarnOtherFixedAPRABI,
        address: contractAddress as `0x${string}`,
        functionName: "deposit",
        args: [BigInt(inputAmount.toString())],
      });
      
      // Wait for transaction to be mined
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });
      
      events.onReceipt({ transactionHash: receipt.transactionHash, status: receipt.status === "success" });
      return { transactionHash: receipt.transactionHash, status: receipt.status === "success" };
    } catch (e) {
      events.onError(e, null);
      return null;
    }
  };

  const withdrawLpToken = async (
    contractAddress: string,
    account: string,
    inputAmount: BigNumber,
    events: Events
  ): Promise<any> => {
    try {
      const hash = await writeContract(wagmiConfig, {
        abi: EarnOtherFixedAPRABI,
        address: contractAddress as `0x${string}`,
        functionName: "withdraw",
        args: [BigInt(inputAmount.toString())],
      });
      
      // Wait for transaction to be mined
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });
      
      events.onReceipt({ transactionHash: receipt.transactionHash, status: receipt.status === "success" });
      return { transactionHash: receipt.transactionHash, status: receipt.status === "success" };
    } catch (e) {
      events.onError(e, null);
      return null;
    }
  };

  const getAPR = async (contractAddress: string): Promise<BigNumber | null> => {
    try {
      const apr = await lazyReadContract(
        EarnOtherFixedAPRABI,
        contractAddress,
        "apr",
        []
      );
      return new BigNumber(apr?.toString() ?? "0");
    } catch (e) {
      return null;
    }
  };

  const getTotalStaked = async (
    contractAddress: string
  ): Promise<BigNumber | null> => {
    try {
      const totalStaked = await lazyReadContract(
        EarnOtherFixedAPRABI,
        contractAddress,
        "totalStaked",
        []
      );
      return new BigNumber(totalStaked?.toString() ?? "0");
    } catch (e) {
      return null;
    }
  };

  const getPendingReward = async (
    contractAddress: string,
    account: string
  ): Promise<BigNumber | null> => {
    try {
      const pendingReward = await lazyReadContract(
        EarnOtherFixedAPRABI,
        contractAddress,
        "pendingReward",
        [account]
      );
      return new BigNumber(pendingReward?.toString() ?? "0");
    } catch (e) {
      console.error("Error: getPendingReward", e);
      return null;
    }
  };

  const getCap = async (contractAddress: string): Promise<BigNumber | null> => {
    try {
      const cap = await lazyReadContract(
        EarnOtherFixedAPRABI,
        contractAddress,
        "cap",
        []
      );
      return new BigNumber(cap?.toString() ?? "0");
    } catch (e) {
      console.error("Error: getCap", e);
      return null;
    }
  };

  const getIsLockWithdraw = async (
    contractAddress: string
  ): Promise<boolean | null> => {
    try {
      const result = await lazyReadContract(
        EarnOtherFixedAPRABI,
        contractAddress,
        "isLockWithdraw",
        []
      );
      return Boolean(result);
    } catch (e) {
      return null;
    }
  };

  const getUserInfo = async (
    contractAddress: string,
    account: string
  ): Promise<any> => {
    try {
      const result: any = await lazyReadContract(
        EarnOtherFixedAPRABI,
        contractAddress,
        "userInfo",
        [account]
      );
      
      if (result) {
        // Handle array format: [amount, rewards, lastRewardBlock]
        let amount = "0";
        if (Array.isArray(result)) {
          amount = result[0]?.toString() || "0";
        } else if (result.amount !== undefined) {
          amount = result.amount.toString();
        }
        
        return {
          amount: BigNumber(amount),
        };
      }
      
      return {
        amount: BigNumber("0"),
      };
    } catch (e) {
      console.error("Error: getUserInfo", e);
      return {
        amount: BigNumber("0"),
      };
    }
  };

  const getStartEndBlock = async (
    contractAddress: string
  ): Promise<{ startBlock: string | null; endBlock: string | null }> => {
    try {
      const [startBlock, endBlock] = await Promise.all([
        lazyReadContract(
          EarnOtherFixedAPRABI,
          contractAddress,
          "startBlock",
          []
        ),
        lazyReadContract(
          EarnOtherFixedAPRABI,
          contractAddress,
          "endBlock",
          []
        ),
      ]);
      return { 
        startBlock: startBlock?.toString() ?? null, 
        endBlock: endBlock?.toString() ?? null 
      };
    } catch (e) {
      console.error("Error: getStartEndBlock", e);
      return { startBlock: null, endBlock: null };
    }
  };

  const getFarmFixedAPRInfo = async (
    index: number,
    poolData?: EarnOtherFixedAPR
  ): Promise<FixApr | null> => {
    try {
      poolData = poolData || config.earnOtherFixedAPR[index];
      if (!poolData) return null;

      const account = walletApi.account.current;
      // Allow fetching farm data without wallet for read-only mode
      // if (account === undefined) return null;

      const promises = [
        getTotalStaked(poolData.contractAddress ?? ""),
        getAPR(poolData.contractAddress ?? ""),
        getCap(poolData.contractAddress ?? ""),
        getIsLockWithdraw(poolData.contractAddress ?? ""),
        getBalanceOfLP(
          poolData.rewardToken ?? "",
          poolData.contractAddress ?? ""
        ),
        getStartEndBlock(poolData.contractAddress ?? ""),
      ];

      const rewardExternalPricePromise = poolData.rewardExternalPrice
        ? [getPriceTokenExternalApi(poolData.rewardSlug ?? "")]
        : [Promise.resolve(null)];

      const extendPromises = account
        ? [
            getBalanceOfLP(poolData.id ?? "", account),
            getUserInfo(poolData.contractAddress ?? "", account),
            getPendingReward(poolData.contractAddress ?? "", account),
          ]
        : [];

      const [
        totalStaked,
        apr,
        cap,
        isLockWithdraw,
        rewardBalance,
        blockData,
        rewardPrice,
        lpBalance,
        userInfo,
        pendingReward,
      ] = await Promise.all([
        ...promises,
        ...rewardExternalPricePromise,
        ...extendPromises,
      ]);

      const { startBlock, endBlock } = blockData;

      let aprCalbyRewardPrice = new BigNumber(apr).div(1e18).div(100);

      if (poolData.rewardExternalPrice) {
        aprCalbyRewardPrice = aprCalbyRewardPrice
          .multipliedBy(rewardPrice || 0)
          .div(poolData.rewardPrice || 0);
      }

      const result: FixApr = {
        ...poolData,
        reward: config.token[poolData.rewardToken ?? ""].name, // Adjust according to your tokenInfo structure
        totalStaked: new BigNumber(totalStaked),
        apr: aprCalbyRewardPrice,
        rewardExternalPrice: poolData.rewardExternalPrice,
        lpPerUsd: new BigNumber(poolData.stakingPrice ?? ""),
        startBlock,
        endBlock,
        cap: new BigNumber(cap),
        rewardBalance: new BigNumber(rewardBalance),
        rewardPrice: rewardPrice || poolData.rewardPrice,
        earnedToken: pendingReward
          ? new BigNumber(pendingReward)
          : new BigNumber("0"),
        lpBalance: lpBalance ? new BigNumber(lpBalance) : new BigNumber("0"),
        balanceInPool: userInfo?.amount || new BigNumber("0"),
        isLockWithdraw,
      };

      return result;
    } catch (e) {
      console.log(`Err: getFarmFixedAPRInfo ${index}`, e);
      return null;
    }
  };

  const getAllFarmFixedAPRInfo = async (): Promise<FixApr[]> => {
    const { earnOtherFixedAPR } = config;

    const result = await earnOtherFixedAPR.reduce(
      async (
        prev: Promise<FixApr[]>,
        cur: EarnOtherFixedAPR,
        index: number
      ) => {
        let acc = await prev;
        
        const response = await getFarmFixedAPRInfo(index, cur);

        if (response) acc = [...acc, response];

        return acc;
      },
      Promise.resolve([])
    );

    return result;
  };

  const getTotalValueLock = async (): Promise<BigNumber> => {
    const { earnOtherFixedAPR } = config;

    const tvl = await earnOtherFixedAPR.reduce(
      async (prev: Promise<BigNumber>, cur: any) => {
        let acc = await prev;
        const response = await getTotalStaked(cur.contractAddress);

        if (response) {
          let stakingPrice = cur.stakingPrice;
          if (!stakingPrice && cur.stakingExternalPrice) {
            stakingPrice = await getPriceTokenExternalApi(cur.stakingSlug);
          }
          acc = acc.plus(new BigNumber(response).multipliedBy(stakingPrice));
        }

        return acc;
      },
      Promise.resolve(new BigNumber("0"))
    );

    return tvl;
  };

  const depositLpTokenWithCheckAllowance = async (
    code: string,
    lpAddress: string,
    contractAddress: string,
    account: string,
    inputAmount: BigNumber,
    events: Events
  ): Promise<void> => {
    const spender = contractAddress;
    const amount = new BigNumber(inputAmount);

    {
      const allowance = await getAllowance(code, account, spender, lpAddress);
      if (allowance && new BigNumber(allowance).isLessThan(amount)) {
        await approveTokenToSpender(lpAddress, account, spender, {
          onTransactionHash: (txHash) => {},
          onReceipt: ({ transactionHash, status }) => {},
          onError: (error: any) => {},
        });
      }

      await depositLpToken(contractAddress, account, inputAmount, events);
    }
  };

  return {
    isReady,
    claimLpToken,
    depositLpToken,
    withdrawLpToken,
    getAPR,
    getTotalStaked,
    getPendingReward,
    getCap,
    getIsLockWithdraw,
    getUserInfo,
    getStartEndBlock,
    getFarmFixedAPRInfo,
    getAllFarmFixedAPRInfo,
    getTotalValueLock,
    depositLpTokenWithCheckAllowance,
  };
};

export default useAprHelper;
